/**
 * Registro del tutorial di benvenuto (story sc-46314).
 *
 * Tutti i contenuti stanno qui, come in `plans.js` e `features.js`: riscrivere
 * il tutorial non deve voler dire aprire un `.vue`. I componenti sono vetro,
 * le decisioni stanno in `utilities/onboarding.js`, e i testi qui.
 */
import { TRIAL_DAYS } from "@/config/plans";

/**
 * Versione del giro guidato.
 *
 * Alzarla ripropone il benvenuto una volta sola, anche a chi aveva saltato.
 * È la valvola che impedisce al tutorial di restare congelato sui primi
 * clienti: senza, chi ha premuto «Salta» a settembre non vedrebbe mai più
 * niente, nemmeno quando il gestionale sarà cambiato.
 */
export const TUTORIAL_VERSION = 1;

/** Le quattro schermate del benvenuto. */
export const PASSI_BENVENUTO = Object.freeze([
  Object.freeze({
    chiave: "benvenuto",
    titolo: "Ci siamo",
    testo:
      "crmstart tiene insieme clienti, listino, preventivi e ordini in un posto solo. " +
      `Hai ${TRIAL_DAYS} giorni per provarlo con i tuoi dati veri: nessuna carta, nessun impegno.`,
    icona: "fa-hand-sparkles",
  }),
  Object.freeze({
    chiave: "listino",
    titolo: "Prima il listino",
    testo:
      "Comincia dagli articoli che vendi: senza listino un preventivo non si scrive. " +
      "Li trovi in Listini, divisi per categoria.",
    icona: "fa-layer-group",
  }),
  Object.freeze({
    chiave: "ciclo",
    titolo: "Dal preventivo all'ordine",
    testo:
      "Un preventivo accettato diventa un ordine, e l'ordine muove il magazzino. " +
      "Il carico merce rimette dentro quello che arriva dai fornitori.",
    icona: "fa-arrow-right-arrow-left",
  }),
  Object.freeze({
    chiave: "aiuto",
    titolo: "Se ti serve una mano",
    testo:
      "In alto trovi Aiuto, con il manuale completo e questo tutorial da " +
      "rivedere. Se preferisci parlarne a voce, mettiti in contatto con il " +
      "nostro Team in due clic.",
    icona: "fa-life-ring",
    // Su questa schermata compaiono i modi veri di raggiungerci. Il flag sta
    // qui e non nel componente perché è una scelta di contenuto: se un giorno
    // il contatto andrà su un'altra schermata, si sposta questa riga.
    contatto: true,
  }),
]);

/**
 * Il pannellino di ogni pagina del menù, per nome di rotta.
 *
 * Le chiavi sono i nomi veri delle rotte di `router/index.js`: un refuso qui
 * non romperebbe niente, il pannellino semplicemente non comparirebbe, ed è
 * per questo che c'è un test che li confronta col router.
 */
export const PANNELLI = Object.freeze({
  home: Object.freeze({
    titolo: "Questa è la tua homepage.",
    testo: "Qui trovi le pagine principali del tuo gestionale.",
  }),
  customers: Object.freeze({
    titolo: "I tuoi clienti.",
    testo:
      "Aggiungi un nuovo cliente per iniziare a usare il gestionale. Considera questa pagina come una rubrica.",
  }),
  listini: Object.freeze({
    titolo: "Il tuo listino.",
    testo:
      "Una scheda per ogni categoria che hai scelto in configurazione. Entra e aggiungi i tuoi articoli.",
  }),
  "listini-category": Object.freeze({
    titolo: "Gli articoli di questa categoria.",
    testo:
      "«Nuovo articolo» per aggiungerne uno: prezzo e unità di vendita si decidono qui, e da qui arrivano nei preventivi.",
  }),
  magazzino: Object.freeze({
    titolo: "Cosa hai in casa.",
    testo:
      "Le giacenze si aggiornano automaticamente con le consegne. Quando arriva nuova merce, aggiorna la quantità dell'articolo.",
  }),
  quotes: Object.freeze({
    titolo: "I preventivi.",
    testo:
      "Ogni preventivo è intestato a un cliente, contiene gli articoli che hai selezionato, è disponibile in formato PDF per l'invio rapido al cliente.",
  }),
  ordini: Object.freeze({
    titolo: "Gli ordini.",
    // [revisione 48637 / sc-48692] Il testo di Elvisia diceva «dall'acconto
    // fino al saldo», e per un po' è stato falso: gli acconti erano spenti
    // dalla 48395. La story 48692 li riaccende, quindi il pannellino torna a
    // raccontare la strada intera. L'acconto però resta una possibilità, non
    // un passaggio obbligato: si può anche pagare tutto in una volta. Il
    // vincolo da dire è l'altro: l'acconto non basta per spedire.
    testo:
      "Qui trovi i preventivi accettati. Hai la possibilità di registrare prima un acconto ed eventualmente il saldo rimanente: dopo l'acconto lo stato del pagamento diventa «Acconto versato», e l'ordine si spedisce solo quando arriva a «Pagato».",
  }),
});

/**
 * I riflettori: due sole schede, i tre passi che contano.
 *
 * `ancora` è il valore dell'attributo `data-tour` sull'elemento da illuminare.
 * Se quell'elemento non c'è, il passo si salta in silenzio — vedi
 * `OnboardingSpotlight`.
 */
export const RIFLETTORI = Object.freeze({
  "add-client": Object.freeze({
    chiave: "scheda-cliente",
    passi: Object.freeze([
      Object.freeze({
        ancora: "client-name",
        testo:
          "Il nome con cui lo ritrovi dappertutto: nei preventivi, negli ordini, nelle ricerche.",
      }),
      Object.freeze({
        ancora: "client-fiscal-code",
        testo:
          "Il codice fiscale serve a intestare il preventivo: senza, il cliente non si salva.",
      }),
      Object.freeze({
        ancora: "client-phone",
        testo:
          "Il telefono è obbligatorio: è il primo modo per richiamarlo quando serve una conferma.",
      }),
      Object.freeze({
        ancora: "client-email",
        testo:
          "L'email è obbligatoria: è l'indirizzo a cui gli mandi il preventivo in PDF.",
      }),
      Object.freeze({
        ancora: "client-save",
        testo:
          "Salva: da qui in poi lo scegli in un preventivo scrivendo le prime lettere del nome.",
      }),
    ]),
  }),
  "listini-add-item": Object.freeze({
    chiave: "scheda-articolo",
    passi: Object.freeze([
      Object.freeze({
        ancora: "item-category",
        testo:
          "Il tipo di articolo decide in quale scheda del listino finisce, e quali campi ti vengono chiesti qui sotto.",
      }),
      Object.freeze({
        ancora: "item-name",
        testo:
          "Il nome è l'unico campo davvero obbligatorio: è quello che cerchi quando compili un preventivo.",
      }),
      Object.freeze({
        ancora: "item-price",
        testo: "Il prezzo che finirà nei preventivi, IVA esclusa.",
      }),
      Object.freeze({
        ancora: "item-sales-unit",
        testo:
          "L'unità di vendita è come lo fatturi: al pezzo, al metro quadro, a scatola.",
      }),
      Object.freeze({
        ancora: "item-typology",
        testo:
          "La tipologia apre i campi giusti per le piastrelle: formato, finitura, pezzi per scatola.",
      }),
      Object.freeze({
        ancora: "item-save",
        testo:
          "Salva: l'articolo entra nel listino ed è pronto per i preventivi.",
      }),
    ]),
  }),
  // [sc-46314, review 48629] Il preventivo è il cuore del gestionale, e qui
  // il giro è lungo apposta: un passo per ogni campo del modulo, dal cliente
  // alla spesa di imballo. Le altre due schede si imparano compilandole; qui
  // ogni campo decide una cifra che finisce in un PDF mandato a un cliente.
  "add-quote": Object.freeze({
    chiave: "scheda-preventivo",
    passi: Object.freeze([
      Object.freeze({
        ancora: "quote-client",
        testo:
          "Scrivi le prime lettere e scegli a chi è intestato il preventivo. Se il cliente non c'è ancora, va creato prima nella pagina Clienti.",
      }),
      Object.freeze({
        ancora: "quote-payment",
        testo:
          "Come ti pagherà: contanti, carta, bonifico. Se non è ancora deciso c'è «Scegli successivamente», e si cambia in ogni momento dal modifica preventivo.",
      }),
      Object.freeze({
        ancora: "quote-date",
        testo:
          "La data a cui fa riferimento il preventivo: è quella che il cliente legge stampata in cima al PDF. Il campo parte vuoto e non blocca il salvataggio, ma un preventivo senza data non si ritrova più.",
      }),
      Object.freeze({
        ancora: "quote-address",
        testo:
          "Dove va consegnata la merce. Si compila solo se hai scelto la consegna: con il ritiro in sede è precompilato.",
      }),
      Object.freeze({
        ancora: "quote-delivery",
        testo:
          "Consegna o ritiro: è obbligatorio, e senza non si salva il preventivo. È anche quello che consente di compilare l'indirizzo di consegna.",
      }),
      Object.freeze({
        ancora: "quote-items",
        testo:
          "Digita le prime lettere di un dettaglio che identifica l'articolo, e selezionalo tra quelli che risultano dalla ricerca.",
      }),
      Object.freeze({
        ancora: "quote-new-item",
        testo:
          "Se l'articolo non risulta dalla ricerca vuol dire che non è ancora a listino: lo crei da qui, senza perdere il preventivo che stai scrivendo. Si apre una finestra, e appena salvato l'articolo viene automaticamente selezionato.",
      }),
      Object.freeze({
        ancora: "quote-quantity",
        testo:
          "Quanto ne serve: a pezzi, oppure a metri quadri se l'articolo si vende a scatola. Nel secondo caso le scatole intere le calcola il gestionale.",
      }),
      Object.freeze({
        ancora: "quote-price",
        testo:
          "Il prezzo di listino dell'articolo, IVA esclusa. Arriva dal listino e non si tocca da qui.",
      }),
      Object.freeze({
        ancora: "quote-discount",
        testo:
          "Il prezzo che fai tu a questo cliente, se è diverso dal listino. Lasciato vuoto, vale quello da listino.",
      }),
      Object.freeze({
        ancora: "quote-vat",
        testo:
          "L'aliquota dell'articolo selezionato. Si decide articolo per articolo, perché non tutto quello che vendi ha la stessa IVA.",
      }),
      Object.freeze({
        ancora: "quote-add-item",
        testo:
          "Aggiungi l'articolo al preventivo. Poi ricominci dalla voce 'Articolo' per aggiungere quello successivo.",
      }),
      Object.freeze({
        ancora: "quote-notes",
        testo:
          "Quello che il cliente deve leggere e che nessun campo dice: tempi, misure da verificare, accordi presi a voce. Finisce sul PDF.",
      }),
      Object.freeze({
        ancora: "quote-packaging",
        testo:
          "La spesa di imballo si aggiunge al totale come voce a sé, il caso in questione prevede 30€ a fini esemplificativi. Mettila a 'Sì' quando la merce va imballata per il trasporto.",
      }),
      Object.freeze({
        ancora: "quote-list",
        testo:
          "Ecco il riepilogo degli articoli aggiunti al preventivo. Da qui è possibile rimuovere gli articoli che si sono aggiunti erroneamente, prima di salvare.",
      }),
      Object.freeze({
        ancora: "quote-save",
        testo:
          "Salva ed esporta: il preventivo si salva e contemporaneamente si genera il PDF, pronto da mandare al cliente. Un preventivo accettato diventa un ordine.",
      }),
    ]),
  }),
});

/**
 * Le voci che esistono solo su CRM PLUS.
 *
 * Sono le funzioni spente in `config/features.js`: non me le sto inventando, e
 * quando una si riaccenderà il posto da aggiornare è uno solo.
 */
export const VOCI_PLUS = Object.freeze([
  Object.freeze({
    chiave: "ddt",
    etichetta: "Consegne parziali, resi e ritiri",
    testo:
      "Più DDT per lo stesso ordine, resi e ritiri, con lo scarico parziale del magazzino.",
    icona: "fa-truck-ramp-box",
  }),
  Object.freeze({
    chiave: "pagamenti",
    etichetta: "Pagamenti avanzati",
    testo: "Saldi in più rate e storico completo dei pagamenti.",
    icona: "fa-money-check-dollar",
  }),
  Object.freeze({
    chiave: "prezzi",
    etichetta: "Modificatori di prezzo",
    testo: "Sconti e maggiorazioni percentuali applicati al preventivo.",
    icona: "fa-percent",
  }),
]);

/**
 * La quarta voce della finestra PLUS.
 *
 * [revisione 48654] Sta fuori da `VOCI_PLUS` di proposito: quelle tre sono
 * funzioni vere, spente da un interruttore di `features.js`, e nel menu
 * Impostazioni compaiono col lucchetto. Questa non e' una funzione da
 * accendere, e' l'invito a chiederne una che non c'e': nel menu sarebbe un
 * pulsante che non apre niente.
 */
export const VOCE_PLUS_SU_MISURA = Object.freeze({
  chiave: "su-misura",
  etichetta: "Personalizzazioni su misura",
  testo:
    "Hai bisogno di una funzione diversa? La progettiamo insieme in base alle esigenze della tua azienda. Esplora con noi le funzionalità PLUS.",
  icona: "fa-screwdriver-wrench",
});

export default {
  TUTORIAL_VERSION,
  PASSI_BENVENUTO,
  PANNELLI,
  RIFLETTORI,
  VOCI_PLUS,
  VOCE_PLUS_SU_MISURA,
};
